import React, { createContext, useCallback, useMemo, useState } from "react";
import Snackbar, { SnackbarProps } from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Slide from "@mui/material/Slide";
import Box from "@mui/material/Box";
import { SYSTEM_BLUE } from "../utils/colors";
import { TransitionProps } from "@mui/material/transitions";
import { NotificationsActive } from "@mui/icons-material";

type NotificationProviderProps = {
  children: React.ReactNode;
};

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide ref={ref} {...props} direction="left" />;
});

export type NotificationContextData = {
  show: (message: string, subtitle?: string, options?: SnackbarProps) => void;
  close: () => void;
};

export const NotificationContext = createContext<NotificationContextData>(
  {} as NotificationContextData
);

const audio = new Audio(
  "https://datasalesio-imagens.s3.amazonaws.com/audio/notification-open-chat.ogg"
);

export function NotificationProvider({ children }: NotificationProviderProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [message, setMessage] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [options, setOptions] = useState<SnackbarProps>({} as SnackbarProps);

  const show = useCallback(
    async (
      message: string,
      subtitle: string,
      notificationOptions: SnackbarProps
    ) => {
      if (notificationOptions) setOptions(notificationOptions);
      setMessage(message);
      setSubtitle(subtitle);
      setIsVisible(true);
      await audio.play();
    },
    []
  );

  const close = useCallback(() => {
    setIsVisible(false);
  }, []);

  const toastContextData = useMemo(() => {
    return {
      show,
      close,
    };
  }, [close, show]);

  return (
    <NotificationContext.Provider value={toastContextData}>
      {children}
      <Snackbar
        {...options}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={isVisible}
        autoHideDuration={3500}
        onClose={close}
        TransitionComponent={Transition}
      >
        <Paper elevation={0}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0}
            p={2}
            bgcolor={SYSTEM_BLUE}
            color="#FFF"
            borderRadius={1}
          >
            <NotificationsActive />
            <Box ml={2}>
              <Typography variant="subtitle2">{message}</Typography>
              {Boolean(subtitle) && (
                <Typography variant="caption">{subtitle}</Typography>
              )}
            </Box>
          </Stack>
        </Paper>
      </Snackbar>
    </NotificationContext.Provider>
  );
}
